import { useQuery } from '@tanstack/react-query';
import { Seo } from '../../../lib/seo';
import { api } from '../../../lib/api';
import type { PublicAnalyticsConfig } from '../../../lib/types';
import { LegalLayout } from '../../../components/LegalLayout';
import { useLegal } from './useLegal';
import { useCookieConsent } from '../../../lib/cookieConsent';
import { useLegalPagesVisible } from '../../../lib/useLegalPagesVisible';

/**
 * One consent-gated analytics provider, rendered only when the super-admin has it enabled.
 * `on` is derived from /platform/analytics (each id is null when the operator disables the
 * tool), so the cookie tables stay in lockstep with Admin → Analytics with no manual edits.
 */
interface AnalyticsTool {
  on: boolean;
  name: string;
  provider: string;
  /** Short function blurb used in the intro sentence. */
  blurb: string;
  cookies: string;
  purpose: string;
  retention: string;
}

/**
 * Cookie Policy. Like the other legal pages the clauses live in code (version-controlled)
 * and interpolate the company identity from /legal/config via useLegal(). The analytics
 * section is DATA-DRIVEN: it fetches /platform/analytics and lists only the providers the
 * super-admin has actually enabled, so it never advertises a tool that isn't running.
 * The "Manage cookie preferences" button re-opens the consent panel so visitors can withdraw.
 */
export function CookiePolicyPage() {
  const c = useLegal();
  const openSettings = useCookieConsent((s) => s.openSettings);
  const { isVisible } = useLegalPagesVisible();
  const subprocessorsPage = isVisible('subprocessors');

  // Same source + query key the analytics loader uses, so it's already cached on most
  // navigations. `auth: false` — this is a public page.
  const { data: analytics } = useQuery({
    queryKey: ['public-analytics-config'],
    queryFn: () => api<PublicAnalyticsConfig>('/platform/analytics', { auth: false }),
    staleTime: 60 * 60 * 1000,
  });

  // Consent-gated tools, in display order. `on` mirrors the enabled flag exactly.
  const consentTools: AnalyticsTool[] = [
    {
      on: Boolean(analytics?.measurementId),
      name: 'Google Analytics 4',
      provider: 'Google Analytics',
      blurb: 'aggregate traffic',
      cookies: '_ga / _ga_*',
      purpose: 'Distinguishes unique visitors + persists GA4 session state.',
      retention: 'Up to 2 years',
    },
    {
      on: Boolean(analytics?.clarityProjectId),
      name: 'Microsoft Clarity',
      provider: 'Microsoft Clarity',
      blurb: 'heatmaps + session replay',
      cookies: '_clck / _clsk',
      purpose: 'Associates a session for heatmaps + replay.',
      retention: 'Up to 1 year',
    },
    {
      on: Boolean(analytics?.posthogApiKey),
      name: 'PostHog',
      provider: 'PostHog',
      blurb: 'product analytics',
      cookies: 'ph_*_posthog',
      purpose: 'Distinguishes users + persists product-analytics state.',
      retention: 'Up to 1 year',
    },
  ];
  const enabledTools = consentTools.filter((t) => t.on);
  const cloudflareOn = Boolean(analytics?.cloudflareToken);
  // Grammatical join: "A", "A and B", "A, B, and C".
  const listProviders = (names: string[]) =>
    names.length <= 1
      ? names.join('')
      : names.length === 2
        ? `${names[0]} and ${names[1]}`
        : `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;

  return (
    <><Seo title="Cookie Policy" description="How SieveGuard uses cookies and similar technologies, what each one does, and how to control your consent." path="/cookies" /><LegalLayout title="Cookie Policy" lastUpdated={c.lastUpdatedAt}>
      <p>
        This Cookie Policy explains how {c.legalEntityName} ("SieveGuard", "we") uses cookies and
        similar technologies (such as <code>localStorage</code>) on our public website. It should be
        read alongside our <a href="/privacy/">Privacy Policy</a>.
      </p>

      <h2>What are cookies?</h2>
      <p>
        Cookies are small text files stored on your device by your browser. Similar technologies
        like <code>localStorage</code> serve the same purpose — keeping small pieces of information
        between page loads. We group everything we set into two categories: strictly necessary and
        analytics.
      </p>

      <h2>Strictly necessary</h2>
      <p>
        These are required for the site and app to function and are exempt from consent under the
        ePrivacy rules. They never carry advertising identifiers.
      </p>
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Purpose</th>
            <th>Type</th>
            <th>Retention</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>sg_access</code> / <code>sg_refresh</code></td>
            <td>Keep you signed in to the app securely.</td>
            <td>localStorage</td>
            <td>Until logout / token expiry</td>
          </tr>
          <tr>
            <td><code>sg_cookie_consent</code></td>
            <td>Remembers your cookie choices so we don't ask again.</td>
            <td>localStorage</td>
            <td>Persistent (until cleared)</td>
          </tr>
          <tr>
            <td><code>theme</code></td>
            <td>Remembers your light/dark theme preference.</td>
            <td>localStorage</td>
            <td>Persistent (until cleared)</td>
          </tr>
          <tr>
            <td><code>sg_ref</code></td>
            <td>First-touch affiliate attribution when you arrive via a partner link.</td>
            <td>localStorage</td>
            <td>60 days</td>
          </tr>
        </tbody>
      </table>

      {enabledTools.length > 0 && (
        <>
          <h2>Analytics (consent required)</h2>
          <p>
            With your consent we load analytics tools to understand aggregate, anonymous usage so
            we can improve the site:{' '}
            {enabledTools.map((t, i) => (
              <span key={t.name}>
                {i > 0 && (i === enabledTools.length - 1 ? ', and ' : ', ')}
                <strong>{t.name}</strong> ({t.blurb})
              </span>
            ))}
            . These cookies are <strong>only</strong> set after you click "Accept" — if you reject
            or ignore the banner, none of them are loaded. Analytics runs on our public pages only,
            never inside the signed-in app.
          </p>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Provider</th>
                <th>Purpose</th>
                <th>Retention</th>
              </tr>
            </thead>
            <tbody>
              {enabledTools.map((t) => (
                <tr key={t.name}>
                  <td><code>{t.cookies}</code></td>
                  <td>{t.provider}</td>
                  <td>{t.purpose}</td>
                  <td>{t.retention}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p>
            {listProviders(enabledTools.map((t) => t.provider))} act as our{' '}
            {enabledTools.length === 1 ? 'processor' : 'processors'} for analytics.
            {subprocessorsPage ? (
              <>
                {' '}See <a href="/subprocessors/">our sub-processors</a> for each provider's
                location, transfer mechanism, and DPA.
              </>
            ) : (
              <>
                {' '}For each provider's location, transfer mechanism, and DPA, contact{' '}
                <code>{c.privacyEmail}</code>.
              </>
            )}
          </p>
        </>
      )}

      {cloudflareOn && (
        <>
          <h2>Cookieless analytics (no consent needed)</h2>
          <p>
            We also use <strong>Cloudflare Web Analytics</strong>, which measures aggregate traffic
            <strong> without cookies</strong> and without storing or reading any information on your
            device. Because it sets no cookies and cannot identify you, it does not require consent
            and runs on all visits.
            {subprocessorsPage && (
              <>
                {' '}See <a href="/subprocessors/">our sub-processors</a>.
              </>
            )}
          </p>
        </>
      )}

      {enabledTools.length === 0 && !cloudflareOn && (
        <>
          <h2>Analytics</h2>
          <p>
            We currently do not load any analytics or tracking tools on this website. Only the
            strictly-necessary items above are ever stored on your device.
          </p>
        </>
      )}

      <h2>Managing your consent</h2>
      <p>
        You can change or withdraw your consent at any time. Use the button below (also available as
        "Cookie settings" in the footer of every page), or clear cookies in your browser settings.
        Withdrawing consent stops analytics from loading on your next page view.
      </p>
      <p>
        <button
          type="button"
          onClick={openSettings}
          className="rounded-md border border-border bg-panel2 px-3 py-1.5 text-[13px] font-medium text-txt transition hover:border-accentdeep"
        >
          Manage cookie preferences
        </button>
      </p>

      <h2>Questions</h2>
      <p>
        For any question about this policy or your data, contact us at <code>{c.privacyEmail}</code>.
      </p>
    </LegalLayout></>
  );
}

// Default export required by React Router framework-mode route modules.
export default CookiePolicyPage;
